/* eslint-disable react/function-component-definition */
import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { TableContext } from '../../contexts/tableContext';
import { Entry } from '../../redux/entrySlice';
import { CapitalizeFirstLetter } from '../../utilityFunctions/utilsFunctions';
import styles from '../../pages/roles/create.module.scss';
import matrixStyles from './PermissionMatrix.module.scss';
import {
  buildPermissionStatusFromMenu,
  grantSectionPermissions,
  isSectionGranted,
  mergeStoredPermissions,
} from './permissionUtils';

const PERMISSIONS = ['create', 'edit', 'view', 'delete'];
const VIEW_ONLY = 'view';
const EXCLUDED_MODULES = new Set(['roles', 'sub-admins', 'sections', 'employee', 'movies']);

const PermissionMatrix = forwardRef(function PermissionMatrix(
  {
    disabled = false,
    modulesActionUrl = 'roles/modules',
    simpleSelection = false,
    initialPermissions = null,
  },
  ref
) {
  const { permissionboxStyle, bodyheader } = useContext(TableContext);
  const dispatch = useDispatch();
  const [sections, setSections] = useState([]);
  const [status, setStatus] = useState([]);

  useEffect(() => {
    const actionData = {
      actionUrl: modulesActionUrl,
      actionMethod: 'get',
    };
    dispatch(Entry(actionData)).then((resp) => {
      const menu = (resp.payload?.data?.Menu || []).filter((item) => !EXCLUDED_MODULES.has(item.modules));
      setSections(menu);
      setStatus(
        initialPermissions?.length
          ? mergeStoredPermissions(menu, initialPermissions)
          : buildPermissionStatusFromMenu(menu)
      );
    });
  }, [dispatch, modulesActionUrl]);

  useEffect(() => {
    if (!sections.length) return;
    setStatus(
      initialPermissions?.length
        ? mergeStoredPermissions(sections, initialPermissions)
        : buildPermissionStatusFromMenu(sections)
    );
  }, [initialPermissions, sections]);

  useImperativeHandle(ref, () => ({
    getPermissions: () => status,
    reset: () =>
      setStatus(
        initialPermissions?.length
          ? mergeStoredPermissions(sections, initialPermissions)
          : buildPermissionStatusFromMenu(sections)
      ),
  }));

  const handleSimpleSectionToggle = useCallback(
    (sectionModule, checked) => {
      const menuSection = sections.find((item) => item.modules === sectionModule);
      setStatus((prev) =>
        prev.map((item) =>
          item.section === sectionModule ? grantSectionPermissions(item, menuSection, checked) : item
        )
      );
    },
    [sections]
  );

  const handleSimpleSelectAll = useCallback(
    (checked) => {
      setStatus((prev) =>
        prev.map((item) => {
          const menuSection = sections.find((section) => section.modules === item.section);
          return grantSectionPermissions(item, menuSection, checked);
        })
      );
    },
    [sections]
  );

  const handleOnChange = useCallback((e, permission, section, subSection, isSubmenu = false) => {
    setStatus((prev) => {
      const next = prev.map((item) => ({
        ...item,
        submenu: item.submenu ? item.submenu.map((sub) => ({ ...sub })) : item.submenu,
      }));

      if (e.target.value === 'select-all') {
        next.forEach((element, index) => {
          const selectAllEl = document.getElementById(`subadmin-select-all-${element.section}`);
          if (selectAllEl) selectAllEl.checked = e.target.checked;
          if (element.submenu) {
            next[index][VIEW_ONLY] = e.target.checked;
            const viewEl = document.getElementById(`subadmin-custom-checkbox-${element.section}-${VIEW_ONLY}`);
            if (viewEl) viewEl.checked = e.target.checked;
            element.submenu.forEach((subItem, subIndex) => {
              PERMISSIONS.forEach((permissionItem) => {
                next[index].submenu[subIndex][permissionItem] = e.target.checked;
                const el = document.getElementById(
                  `subadmin-custom-checkbox-${element.section}-${subItem.section}-${permissionItem}`
                );
                if (el) el.checked = e.target.checked;
              });
            });
          } else if (element.section === 'dashboard') {
            next[index][VIEW_ONLY] = e.target.checked;
            const viewEl = document.getElementById(`subadmin-custom-checkbox-${element.section}-${VIEW_ONLY}`);
            if (viewEl) viewEl.checked = e.target.checked;
          } else {
            PERMISSIONS.forEach((permissionItem) => {
              next[index][permissionItem] = e.target.checked;
              const el = document.getElementById(`subadmin-custom-checkbox-${element.section}-${permissionItem}`);
              if (el) el.checked = e.target.checked;
            });
          }
        });
        return next;
      }

      if (e.target.value === `select-all-${section}`) {
        next.forEach((element, index) => {
          if (element.section !== section) return;
          if (element.submenu) {
            next[index][VIEW_ONLY] = e.target.checked;
            const viewEl = document.getElementById(`subadmin-custom-checkbox-${section}-${VIEW_ONLY}`);
            if (viewEl) viewEl.checked = e.target.checked;
            element.submenu.forEach((subItem, subIndex) => {
              PERMISSIONS.forEach((permissionItem) => {
                next[index].submenu[subIndex][permissionItem] = e.target.checked;
                const el = document.getElementById(
                  `subadmin-custom-checkbox-${section}-${subItem.section}-${permissionItem}`
                );
                if (el) el.checked = e.target.checked;
              });
            });
          } else if (element.section === 'dashboard') {
            next[index][VIEW_ONLY] = e.target.checked;
            const viewEl = document.getElementById(`subadmin-custom-checkbox-${element.section}-${VIEW_ONLY}`);
            if (viewEl) viewEl.checked = e.target.checked;
          } else {
            PERMISSIONS.forEach((permissionItem) => {
              next[index][permissionItem] = e.target.checked;
              const el = document.getElementById(`subadmin-custom-checkbox-${section}-${permissionItem}`);
              if (el) el.checked = e.target.checked;
            });
          }
        });
        return next;
      }

      next.forEach((element, index) => {
        if (element.section !== section) return;
        if (isSubmenu) {
          element.submenu.forEach((subItem, subIndex) => {
            if (subItem.section === subSection) {
              next[index].submenu[subIndex][permission] = e.target.checked;
            }
          });
        } else {
          next[index][permission] = e.target.checked;
        }
      });
      return next;
    });
  }, []);

  if (!sections.length) {
    return <p className={styles.formlabel}>Loading dashboard menus…</p>;
  }

  const allSelected =
    simpleSelection &&
    sections.length > 0 &&
    sections.every((section) => {
      const item = status.find((entry) => entry.section === section.modules);
      return item && isSectionGranted(item, section);
    });

  if (simpleSelection) {
    return (
      <div className="mt-3">
        <p className={styles.formlabel}>
          Dashboard menus <span style={{ color: 'red' }}>*</span>
        </p>
        <p className={styles.selectlabel} style={{ marginBottom: 12 }}>
          Select which sections this sub-admin can access. Full access is granted for each selected menu.
        </p>
        <div className={`row mb-2 ${matrixStyles.simpleMenuList}`}>
          <div className="col-12" style={{ padding: '0 12px' }}>
            <div className={matrixStyles.simpleSelectAll}>
              <input
                type="checkbox"
                id="subadmin-select-all"
                className={matrixStyles.simpleCheckbox}
                checked={allSelected}
                disabled={disabled}
                onChange={(e) => handleSimpleSelectAll(e.target.checked)}
              />
              <label htmlFor="subadmin-select-all" className={matrixStyles.simpleLabel}>
                Select all
              </label>
            </div>
          </div>
        </div>
        <div className={`row ${matrixStyles.simpleMenuList}`}>
          {sections.map((section) => {
            const permissionItem = status.find((entry) => entry.section === section.modules);
            const checked = permissionItem ? isSectionGranted(permissionItem, section) : false;
            return (
              <div className="col-md-6 col-sm-12" key={section.modules}>
                <div className={matrixStyles.simpleMenuRow}>
                  <input
                    type="checkbox"
                    id={`subadmin-menu-${section.modules}`}
                    className={matrixStyles.simpleCheckbox}
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => handleSimpleSectionToggle(section.modules, e.target.checked)}
                  />
                  <label htmlFor={`subadmin-menu-${section.modules}`} className={matrixStyles.simpleLabel}>
                    {CapitalizeFirstLetter(section.name)}
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <p className={styles.formlabel}>
        Dashboard menus <span style={{ color: 'red' }}>*</span>
      </p>
      <p className={styles.selectlabel} style={{ marginBottom: 12 }}>
        Select which sections this sub-admin can access after login.
      </p>
      <div className="row">
        <div className="col-12" style={{ padding: '0 12px' }}>
          <input
            type="checkbox"
            id="subadmin-select-all"
            name="select-all"
            value="select-all"
            disabled={disabled}
            onChange={(e) => handleOnChange(e, 'all', 'all')}
          />
          <label htmlFor="subadmin-select-all" className={styles.selectlabel}>
            Select all
          </label>
        </div>
      </div>
      <br />
      {sections.map((value) => (
        <div className="row" key={value.modules}>
          <div className="col-md-6 col-sm-12">
            <div className={styles[permissionboxStyle]} id={styles.permissionbox}>
              <div className={styles.permission_title}>
                <input
                  type="checkbox"
                  id={`subadmin-select-all-${value.modules}`}
                  name={`select-all-${value.modules}`}
                  value={`select-all-${value.modules}`}
                  disabled={disabled}
                  onChange={(e) => handleOnChange(e, 'all', value.modules)}
                />
                <h5 className={styles[bodyheader]} id={styles.addheading}>
                  {CapitalizeFirstLetter(value.name)}
                </h5>
              </div>
              {value.name === 'Dashboard' || value.subMenu ? (
                <ul>
                  <li>
                    <input
                      type="checkbox"
                      id={`subadmin-custom-checkbox-${value.modules}-${VIEW_ONLY}`}
                      name={VIEW_ONLY}
                      value={VIEW_ONLY}
                      disabled={disabled}
                      onChange={(e) => handleOnChange(e, VIEW_ONLY, value.modules)}
                    />
                    <label
                      htmlFor={`subadmin-custom-checkbox-${value.modules}-${VIEW_ONLY}`}
                      className={styles.selectlabel}
                    >
                      {VIEW_ONLY}
                    </label>
                  </li>
                </ul>
              ) : (
                <ul>
                  {PERMISSIONS.map((name) => (
                    <li key={name}>
                      <input
                        type="checkbox"
                        id={`subadmin-custom-checkbox-${value.modules}-${name}`}
                        name={name}
                        value={name}
                        disabled={disabled}
                        onChange={(e) => handleOnChange(e, name, value.modules)}
                      />
                      <label
                        htmlFor={`subadmin-custom-checkbox-${value.modules}-${name}`}
                        className={styles.selectlabel}
                      >
                        {name}
                      </label>
                    </li>
                  ))}
                </ul>
              )}

              {value.subMenu ? (
                <div className="mt-3 mb-3">
                  <h5 className={styles[bodyheader]} id={styles.addheading}>
                    {CapitalizeFirstLetter('submenu')}
                  </h5>
                </div>
              ) : null}

              {value.subMenu
                ? value.subMenu.map((subvalue) => (
                    <div className="row" key={subvalue.modules}>
                      <div className="col-md-12 col-sm-12">
                        <div className={styles[permissionboxStyle]} id={styles.permissionbox}>
                          <h5 className={styles[bodyheader]} id={styles.addheading}>
                            {CapitalizeFirstLetter(subvalue.name)}
                          </h5>
                          <ul className="mt-3">
                            {PERMISSIONS.map((name) => (
                              <li key={name}>
                                <input
                                  type="checkbox"
                                  id={`subadmin-custom-checkbox-${value.modules}-${subvalue.modules}-${name}`}
                                  name={name}
                                  value={name}
                                  disabled={disabled}
                                  onChange={(e) =>
                                    handleOnChange(e, name, value.modules, subvalue.modules, true)
                                  }
                                />
                                <label
                                  htmlFor={`subadmin-custom-checkbox-${value.modules}-${subvalue.modules}-${name}`}
                                  className={styles.selectlabel}
                                >
                                  {name}
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))
                : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

PermissionMatrix.propTypes = {
  disabled: PropTypes.bool,
  modulesActionUrl: PropTypes.string,
  simpleSelection: PropTypes.bool,
  initialPermissions: PropTypes.arrayOf(PropTypes.object),
};

export default PermissionMatrix;
